import { computed } from 'vue'
import { defineStore } from 'pinia'
import useSystemData from './composables/useSystemData'
import type { OrderWSUpdate } from '@trading/infrastructure'
import type { CreateOrderDTO, ID, OrderModel, UpdateOrderDTO } from '@trading/core'
import { useQueryCache } from '@pinia/colada'
import { useAccountStore } from './Account'
import { createOrdersQueryOptions, getOrdersQueryKey, ORDERS_QUERY_KEY } from '~/queries/orders'

export const useOrderStore = defineStore('order', () => {
  const queryCache = useQueryCache()
  const { isActual, isLoadding } = useSystemData()
  const accountStore = useAccountStore()

  function getWsOrderService() {
    return useNuxtApp().$wsOrderService
  }

  const allOrders = computed(() => {
    const accountId = accountStore.currentAccount?.id

    return accountId == null
      ? []
      : (queryCache.getQueryData<OrderModel[]>(getOrdersQueryKey(accountId)) ?? [])
  })

  function resetOrdersQueryCache() {
    queryCache.cancelQueries({
      key: [ORDERS_QUERY_KEY]
    })

    queryCache
      .getEntries({
        key: [ORDERS_QUERY_KEY]
      })
      .forEach((entry) => {
        queryCache.remove(entry)
      })
  }

  async function subscribeWs() {
    return getWsOrderService().subscribeToOrdersUpdate((data: OrderWSUpdate) => {
      updateOrder(data.order, data.event)
    })
  }

  function unsubscribe() {
    const service = getWsOrderService()
    if (service) {
      service.unsubscribe()
    }
  }

  async function fetchAllOrders(
    accountId: ID | null = accountStore.currentAccount?.id ?? null
  ): Promise<OrderModel[]> {
    if (accountId == null) {
      isActual.value = false
      return []
    }

    const key = getOrdersQueryKey(accountId)
    isLoadding.value = true

    // if (isActual.value) {
    //   return new Promise((resolve) => {
    //     resolve(allOrders.value)
    //     isLoadding.value = false
    //   })
    // }

    try {
      const entry = queryCache.ensure(createOrdersQueryOptions(accountId))
      const state = await queryCache.fetch(entry)

      if (state.status === 'error') {
        throw state.error
      }

      return queryCache.getQueryData<OrderModel[]>(key) ?? []
    } finally {
      isActual.value = true
      isLoadding.value = false
    }
  }

  function updateOrder(
    order: OrderModel,
    event: 'created' | 'filled' | 'updated' | 'cancelled' | 'partially_filled'
  ) {
    const key = getOrdersQueryKey(order.account_id)
    const cachedOrders = queryCache.getQueryData<OrderModel[]>(key)
    if (!Array.isArray(cachedOrders)) return

    const orders = [...cachedOrders]
    const index = orders.findIndex((item) => item.id === order.id)
    if (event === 'created' && index === -1) {
      orders.push(order)
    } else if ((event === 'partially_filled' || event === 'updated') && index !== -1) {
      orders[index] = order
    } else if ((event === 'filled' || event === 'cancelled') && index !== -1) {
      orders.splice(index, 1)
    }
    queryCache.setQueryData(key, orders)
  }

  function createOrder(dto: CreateOrderDTO) {
    return getWsOrderService().createOrder(dto)
  }

  async function patchOrder(dto: UpdateOrderDTO, id: number) {
    return getWsOrderService().updateOrder(dto, id)
  }

  async function closeOrder(id: ID): Promise<void> {
    const accountId = accountStore.currentAccount?.id
    await getWsOrderService().closeOrder(id)
    if (accountId == null) return

    const key = getOrdersQueryKey(accountId)
    const orders = queryCache.getQueryData<OrderModel[]>(key)
    if (!Array.isArray(orders)) return

    queryCache.setQueryData(
      key,
      orders.filter((order) => order.id !== id)
    )
  }

  async function cancelOrdersBySymbol(symbol: string, expectedCount: number): Promise<number> {
    const normalizedSymbol = symbol.trim()
    if (!normalizedSymbol) {
      throw new Error('Order symbol is required.')
    }

    const accountId = accountStore.currentAccount?.id
    if (accountId == null) {
      throw new Error('Current account is required to cancel orders.')
    }

    const cancelledCount = await getWsOrderService().cancelOrdersBySymbol(normalizedSymbol)

    if (cancelledCount !== expectedCount) {
      await fetchAllOrders(accountId)
      return cancelledCount
    }

    const key = getOrdersQueryKey(accountId)
    const orders = queryCache.getQueryData<OrderModel[]>(key)

    if (Array.isArray(orders)) {
      queryCache.setQueryData(
        key,
        orders.filter((order) => order.symbol !== normalizedSymbol)
      )
    }

    return cancelledCount
  }

  function reset() {
    isActual.value = false
    isLoadding.value = false

    resetOrdersQueryCache()
  }

  return {
    fetchAllOrders,
    allOrders,
    isLoadding,
    closeOrder,
    cancelOrdersBySymbol,
    createOrder,
    updateOrder,
    patchOrder,
    subscribeToOrders: subscribeWs,
    unsubscribe,
    reset
  }
})
